import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const client = axios.create({
    baseURL: API_BASE,
    timeout: 120000,
});

export const api = {
    // projects
    listProjects: () => client.get("/projects").then((r) => r.data),
    createProject: (data) => client.post("/projects", data).then((r) => r.data),
    getProject: (id) => client.get(`/projects/${id}`).then((r) => r.data),
    updateProject: (id, data) =>
        client.patch(`/projects/${id}`, data).then((r) => r.data),
    deleteProject: (id) => client.delete(`/projects/${id}`).then((r) => r.data),

    // budgets
    setBudgets: (projectId, budgets) =>
        client.put(`/projects/${projectId}/budgets`, budgets).then((r) => r.data),
    getBudgetSummary: (projectId) =>
        client.get(`/projects/${projectId}/budget-summary`).then((r) => r.data),
    suggestComplementary: (projectId, trade, model = "claude") =>
        client
            .post(
                `/projects/${projectId}/budget/${encodeURIComponent(trade)}/suggest-complementary`,
                null,
                { params: { model }, timeout: 60000 }
            )
            .then((r) => r.data),
    setBestOverride: (projectId, trade, amount) =>
        client
            .put(
                `/projects/${projectId}/best-override/${encodeURIComponent(trade)}`,
                { amount }
            )
            .then((r) => r.data),
    normalizeTrades: (projectId) =>
        client
            .post(`/projects/${projectId}/normalize-trades`)
            .then((r) => r.data),

    // devis
    listDevis: (projectId) =>
        client.get(`/projects/${projectId}/devis`).then((r) => r.data),
    createDevis: (projectId, data) =>
        client.post(`/projects/${projectId}/devis`, data).then((r) => r.data),
    getDevis: (id) => client.get(`/devis/${id}`).then((r) => r.data),
    updateDevis: (id, data) =>
        client.patch(`/devis/${id}`, data).then((r) => r.data),
    deleteDevis: (id) => client.delete(`/devis/${id}`).then((r) => r.data),
    devisPdfUrl: (id) => `${API_BASE}/devis/${id}/pdf`,
    uploadDevis: (projectId, file, model = "claude") => {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("model", model);
        return client
            .post(`/projects/${projectId}/devis/upload`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 180000,
            })
            .then((r) => r.data);
    },

    // revisions
    listRevisions: (devisId) =>
        client.get(`/devis/${devisId}/revisions`).then((r) => r.data),
    duplicateAsRevision: (devisId, revisionNote) =>
        client
            .post(`/devis/${devisId}/duplicate`, { revision_note: revisionNote || null })
            .then((r) => r.data),
    uploadRevision: (devisId, file, model = "claude", revisionNote = null) => {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("model", model);
        if (revisionNote) fd.append("revision_note", revisionNote);
        return client
            .post(`/devis/${devisId}/revisions/upload`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 180000,
            })
            .then((r) => r.data);
    },
    activateVersion: (devisId) =>
        client.post(`/devis/${devisId}/activate`).then((r) => r.data),
    forceOCR: (devisId, model = "claude") =>
        client
            .post(`/devis/${devisId}/reocr`, null, {
                params: { model },
                timeout: 180000,
            })
            .then((r) => r.data),
    signDevis: (devisId, signNote = null) =>
        client.post(`/devis/${devisId}/sign`, { sign_note: signNote || null }).then((r) => r.data),
    unsignDevis: (devisId) =>
        client.post(`/devis/${devisId}/unsign`).then((r) => r.data),
    marketStudy: (params = {}) =>
        client.get(`/market-study`, { params }).then((r) => r.data),

    // comparison & duplicates
    getComparison: (projectId) =>
        client.get(`/projects/${projectId}/comparison`).then((r) => r.data),
    createManualGroup: (projectId, itemIds, label) =>
        client
            .post(`/projects/${projectId}/manual-groups`, {
                item_ids: itemIds,
                label: label || null,
            })
            .then((r) => r.data),
    unmergeItems: (projectId, itemIds) =>
        client
            .post(`/projects/${projectId}/manual-groups/unmerge`, {
                item_ids: itemIds,
            })
            .then((r) => r.data),
    updateLineItem: (devisId, itemId, payload) =>
        client
            .patch(`/devis/${devisId}/line-items/${itemId}`, payload)
            .then((r) => r.data),
    detectDuplicates: (projectId, model = "claude") =>
        client
            .post(`/projects/${projectId}/detect-duplicates`, null, {
                params: { model },
                timeout: 180000,
            })
            .then((r) => r.data),
    listDuplicates: (projectId) =>
        client.get(`/projects/${projectId}/duplicates`).then((r) => r.data),

    // omissions (postes oubliés)
    detectOmissions: (projectId, model = "claude") =>
        client
            .post(`/projects/${projectId}/detect-omissions`, null, {
                params: { model },
                timeout: 240000,
            })
            .then((r) => r.data),
    listOmissions: (projectId) =>
        client.get(`/projects/${projectId}/omissions`).then((r) => r.data),
    dismissOmission: (projectId, alertId) =>
        client
            .delete(`/projects/${projectId}/omissions/${alertId}`)
            .then((r) => r.data),

    // chat assistant
    listChat: (projectId) =>
        client.get(`/projects/${projectId}/chat`).then((r) => r.data),
    sendChat: (projectId, content, model = "claude") =>
        client
            .post(
                `/projects/${projectId}/chat`,
                { content, model },
                { timeout: 120000 }
            )
            .then((r) => r.data),
    clearChat: (projectId) =>
        client.delete(`/projects/${projectId}/chat`).then((r) => r.data),

    // negotiation
    negotiate: (devisId, payload) =>
        client.post(`/devis/${devisId}/negotiate`, payload, { timeout: 180000 }).then((r) => r.data),

    // messages
    listMessages: (devisId) =>
        client.get(`/devis/${devisId}/messages`).then((r) => r.data),
    addMessage: (devisId, payload) =>
        client.post(`/devis/${devisId}/messages`, payload).then((r) => r.data),
    deleteMessage: (id) => client.delete(`/messages/${id}`).then((r) => r.data),

    // public links
    createPublicLink: (projectId, payload) =>
        client.post(`/projects/${projectId}/public-link`, payload).then((r) => r.data),
    listPublicLinks: (projectId) =>
        client.get(`/projects/${projectId}/public-links`).then((r) => r.data),
    getPublicQuote: (token) =>
        client.get(`/public/quote/${token}`).then((r) => r.data),
    submitPublicQuote: (token, payload) =>
        client.post(`/public/quote/${token}/submit`, payload).then((r) => r.data),

    // CCTP
    cctpUrl: (projectId, trade) => {
        const q = trade ? `?trade=${encodeURIComponent(trade)}` : "";
        return `${API_BASE}/projects/${projectId}/cctp.pdf${q}`;
    },

    // Comparison exports
    comparisonXlsxUrl: (projectId) =>
        `${API_BASE}/projects/${projectId}/comparison.xlsx`,
    comparisonPdfUrl: (projectId) =>
        `${API_BASE}/projects/${projectId}/comparison.pdf`,
};

export const fmtEUR = (n) => {
    if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
    return new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 2,
    }).format(Number(n));
};

export const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    } catch {
        return iso;
    }
};

export const TRADE_LABELS = {
    plombier: "Plomberie",
    electricien: "Électricité",
    terrassier: "Terrassement",
    macon: "Maçonnerie",
    maçon: "Maçonnerie",
    menuisier: "Menuiserie",
    menuisier_int: "Menuiserie intérieure",
    menuisier_ext: "Menuiserie extérieure",
    peintre: "Peinture",
    carreleur: "Carrelage / Faïence",
    couvreur: "Couverture",
    charpentier: "Charpente",
    chauffagiste: "Chauffage / Climatisation",
    platrier: "Plâtrerie / Placo",
    plâtrier: "Plâtrerie / Placo",
    plaquiste: "Plâtrerie / Placo",
    isolation: "Isolation",
    etancheite: "Étanchéité",
    facadier: "Façade / Ravalement",
    cuisiniste: "Cuisine",
    cuisine: "Cuisine",
    salle_de_bain: "Salle de bain",
    domotique: "Domotique",
    vitrier: "Vitrerie / Miroiterie",
    serrurier: "Serrurerie",
    ascensoriste: "Ascenseur",
    paysagiste: "Espaces verts",
    demolisseur: "Démolition",
    desamiantage: "Désamiantage",
    vrd: "VRD / Voirie",
    sols: "Sols souples",
    non_classe: "Non classé",
};

// Ordered list shown in dropdowns
export const TRADE_OPTIONS = [
    { value: "terrassier", label: "Terrassement" },
    { value: "vrd", label: "VRD / Voirie" },
    { value: "demolisseur", label: "Démolition" },
    { value: "desamiantage", label: "Désamiantage" },
    { value: "macon", label: "Maçonnerie" },
    { value: "charpentier", label: "Charpente" },
    { value: "couvreur", label: "Couverture" },
    { value: "etancheite", label: "Étanchéité" },
    { value: "isolation", label: "Isolation" },
    { value: "facadier", label: "Façade / Ravalement" },
    { value: "platrier", label: "Plâtrerie / Placo" },
    { value: "menuisier_ext", label: "Menuiserie extérieure" },
    { value: "menuisier_int", label: "Menuiserie intérieure" },
    { value: "plombier", label: "Plomberie" },
    { value: "electricien", label: "Électricité" },
    { value: "chauffagiste", label: "Chauffage / Climatisation" },
    { value: "domotique", label: "Domotique" },
    { value: "carreleur", label: "Carrelage / Faïence" },
    { value: "sols", label: "Sols souples" },
    { value: "peintre", label: "Peinture" },
    { value: "vitrier", label: "Vitrerie / Miroiterie" },
    { value: "serrurier", label: "Serrurerie" },
    { value: "cuisine", label: "Cuisine" },
    { value: "salle_de_bain", label: "Salle de bain" },
    { value: "ascensoriste", label: "Ascenseur" },
    { value: "paysagiste", label: "Espaces verts" },
    { value: "non_classe", label: "Autre / Non classé" },
];

export const tradeLabel = (t) =>
    TRADE_LABELS[(t || "").toLowerCase()] || (t || "Non classé");
